import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, injectIntl, useIntl} from 'react-intl';

import {Button, Dialog, DialogHeader, DialogBody, DialogFooter, Select} from '@stubhub/bamboo';
import {NONE_CURRENCY_VALUE} from '@stubhub/currency-utils';
import {currenciesMessages} from '@stubhub/react-store-default-currency';

import AdvisoryCurrency from './AdvisoryCurrency';
import messages from './messages';
import StoreConfigModalSubtitle from './ModalSubtitle';

import '../styles/index.scss';

/**
 * Modal that allows to change locale and advisory currency
 */
const StoreConfigModal = ({visible, currencies, locales, currentLocale, currentCurrency, onClose, onSubmit}) => {
  const intl = useIntl();
  const [selectedLocale, setSelectedLocale] = useState(currentLocale);
  const [selectedCurrency, setSelectedCurrency] = useState(currentCurrency || NONE_CURRENCY_VALUE);
  const hasCurrencies = currencies && currencies.length > 0;
  const hasLocales = locales && locales.length > 0;

  useEffect(() => {
    setSelectedLocale(currentLocale);
  }, [currentLocale]);

  useEffect(() => {
    setSelectedCurrency(currentCurrency || NONE_CURRENCY_VALUE);
  }, [currentCurrency]);

  const handleLocaleChange = (event) => {
    setSelectedLocale(event.target.value);
  };

  const handleCurrencyChange = (event) => {
    setSelectedCurrency(event.target.value);
  };

  const handleSubmit = () => {
    if (onSubmit) {
      const submitValues = {
        ...(hasCurrencies && {currency: selectedCurrency}),
        ...(hasLocales && {locale: selectedLocale}),
      };

      onSubmit(submitValues);
    }
  };

  const handleClose = () => {
    setSelectedLocale(currentLocale);
    setSelectedCurrency(currentCurrency || NONE_CURRENCY_VALUE);

    typeof onClose === 'function' && onClose();
  };

  const getCurrenciesWithNone = () => {
    if (hasCurrencies) {
      return [
        {
          label: intl.formatMessage(messages.noCurrency),
          value: NONE_CURRENCY_VALUE,
        },
        ...currencies.map((c) => ({
          ...c,
          label: intl.formatMessage(currenciesMessages[c.value]),
        })),
      ];
    }

    return [];
  };
  const currenciesWithNone = getCurrenciesWithNone();

  if (!visible || (!hasCurrencies && !hasLocales)) {
    return null;
  }

  return (
    <Dialog
      className="store-config-modal__container"
      id="store-config-modal"
      isOpen={visible}
      closeOnOverlayClick
      closeOnEsc
      onDismiss={handleClose}
    >
      <DialogHeader className="store-config-modal__header" closeButton>
        <FormattedMessage id="StoreConfigModal.title" defaultMessage="Configure your store" />
      </DialogHeader>
      <DialogBody className="store-config-modal__body">
        <>
          <p className="store-config-modal__header__subtitle" data-testid="store-config-modal-subtitle">
            <StoreConfigModalSubtitle hasCurrencies={hasCurrencies} hasLocales={hasLocales} />
          </p>
          {hasLocales && (
            <Select
              className="store-config-modal__locales"
              id="storeLocale"
              name="storeLocale"
              labelText={intl.formatMessage(messages.languageInput)}
              options={locales}
              onChange={handleLocaleChange}
              value={selectedLocale}
            />
          )}
          {hasCurrencies ? (
            <>
              <Select
                className="store-config-modal__currencies"
                labelText={intl.formatMessage(messages.currenciesInput)}
                name="storeCurrency"
                id="storeCurrency"
                options={currenciesWithNone}
                value={selectedCurrency}
                onChange={handleCurrencyChange}
                data-testid="store-config-modal-currency-select"
              />

              <AdvisoryCurrency />
            </>
          ) : null}
        </>
      </DialogBody>
      <DialogFooter className="store-config-modal__actions">
        <Button className="store-config-modal__submit" onClick={handleSubmit}>
          <FormattedMessage id="StoreConfigModal.save" defaultMessage="Save" />
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

StoreConfigModal.propTypes = {
  /** Available currencies array or empty if it shouldn't be shown */
  currencies: PropTypes.array,

  /**
   * Available locales array or empty if it shouldn't be shown
   */
  locales: PropTypes.array,

  /**
   * Current selected currency
   */
  currentCurrency: PropTypes.string,

  /**
   * Current selected locale
   */
  currentLocale: PropTypes.string,

  /**
   * If the modal is visible or not
   */
  visible: PropTypes.bool,

  /**
   * Function to invoke on modal submit
   */
  onSubmit: PropTypes.func,

  /**
   * Function to invoke on modal close
   */
  onClose: PropTypes.func,
};

export default injectIntl(StoreConfigModal);
